import React from 'react';
import { useSelector } from 'react-redux';
import { 
    BottomNavigation, 
    BottomNavigationAction, 
    Paper
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HistoryIcon from '@mui/icons-material/History';

/**
 * Bottom navigation bar for PWA mode - provides app-like navigation experience
 */
const BottomNavBar = () => {
    const customerKey = useSelector((state) => state.auth.activeUnit.CustomerKey);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab based on current path
    const getActiveTab = () => {
        const path = location.pathname;
        if (path === '/dashboard') return 0;
        if (path.includes(`/caregivers/${customerKey}/changes`)) return 3;
        if (path.includes(`/caregivers/${customerKey}/add`)) return 2;
        if (path.includes(`/caregivers/${customerKey}`)) return 1;
        return 0; // Default to dashboard
    };

    const handleChange = (event, newValue) => {
        const currentPath = location.pathname;
        
        switch (newValue) {
            case 0: // Dashboard
                if (currentPath !== '/dashboard') {
                    navigate('/dashboard');
                }
                break;
            case 1: // Manage Caregivers
                const managePath = `/caregivers/${customerKey}`;
                if (currentPath !== managePath) {
                    navigate(managePath);
                }
                break;
            case 2: // Add Caregiver
                const addPath = `/caregivers/${customerKey}/add`;
                if (currentPath !== addPath) {
                    navigate(addPath);
                }
                break;
            case 3: // Recent Changes
                const changesPath = `/caregivers/${customerKey}/changes`;
                if (currentPath !== changesPath) {
                    navigate(changesPath);
                }
                break;
            default:
                if (currentPath !== '/dashboard') {
                    navigate('/dashboard');
                }
        }
    };

    return (
        <Paper 
            sx={{ 
                position: 'fixed', 
                bottom: 0, 
                left: 0, 
                right: 0, 
                zIndex: 1000,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                backgroundColor: 'background.paper',
                backdropFilter: 'blur(10px)',
                paddingBottom: 'env(safe-area-inset-bottom)'
            }} 
            elevation={8}
        >
            <BottomNavigation
                value={getActiveTab()}
                onChange={handleChange}
                showLabels
                sx={{
                    height: 70,
                    backgroundColor: 'transparent',
                    '& .MuiBottomNavigationAction-root': {
                        minWidth: 'auto',
                        paddingTop: 1,
                        paddingBottom: 1,
                        color: 'text.secondary',
                        transition: 'all 0.2s ease-in-out',
                        '&.Mui-selected': {
                            color: 'primary.main',
                            transform: 'scale(1.05)',
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }
                        },
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: '0.7rem',
                            marginTop: '4px',
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.3rem',
                        }
                    }
                }}
            >
                <BottomNavigationAction 
                    label="Status" 
                    icon={<HomeIcon />} 
                />
                <BottomNavigationAction 
                    label="Caregivers" 
                    icon={<PeopleAltIcon />} 
                />
                <BottomNavigationAction 
                    label="Add" 
                    icon={<PersonAddIcon />} 
                />
                <BottomNavigationAction 
                    label="History" 
                    icon={<HistoryIcon />} 
                />
            </BottomNavigation>
        </Paper>
    );
};

export default BottomNavBar;